// Shared body-scroll lock, used by every MAKC full-page overlay
// (makc-product-sheet.js, makc-modal-notify-me.js, makc-filter-sheet.js).
//
// overflow:hidden on body alone is known to be unreliable for touch
// scroll on iOS Safari -- pinning body to position:fixed (removing it
// from the flow entirely, not just clipping its overflow) is the robust
// cross-browser way to actually stop the page underneath from scrolling
// while an overlay is open. The negative top offset compensates so the
// page doesn't visually jump to its top on lock; scrollTo restores the
// real position once nothing is locking anymore.
//
// Ref-counted rather than a plain boolean, and shared (not duplicated
// per overlay) for a correctness reason, not just DRY: the filter sheet
// is a partial bottom sheet, not full-screen, so a card or the toolbar
// can still be reachable underneath it -- a user can plausibly open the
// filter sheet, then tap a still-visible card's plus button or
// notify-me pill, opening a second overlay while the first is still
// open. Only a single shared counter coordinates that correctly: the
// second lock() is a no-op (scroll position was already captured by the
// first), and unlock() only actually restores once every overlay that
// locked has also unlocked. Three independent per-file counters
// couldn't know about each other and would unlock prematurely.
window.makcScrollLock = (function () {
  var lockedScrollY = 0;
  var lockCount = 0;

  // Single source of truth for "some overlay is open", exposed as a
  // class on <html> so CSS can react without any overlay needing to
  // know about the others. It is derived from this counter rather than
  // tracked separately: every MAKC overlay already locks scroll on
  // open and unlocks on close, so the counter is exactly the state a
  // consumer wants. Classes, not attributes on the consumers, so each
  // consumer's own hidden state -- an empty cart, for the pill -- stays
  // untouched and independent, and an empty-cart pill stays hidden
  // after an overlay closes while a filled one comes back.
  var OVERLAY_OPEN_CLASS = 'makc-overlay-open';

  // Overlays that animate their exit signal it here when the exit
  // STARTS, so a consumer can react while the panel is still sliding.
  // Counted rather than a boolean because overlays nest for real: the
  // Time Picker opens over the cart, and while the picker closes the
  // cart is still open, so an overlay is still up.
  var closingCount = 0;

  // Two classes, because the two consumers want genuinely different
  // questions answered and one class cannot answer both.
  //
  // makc-overlay-open -- "an overlay is up", true for the whole
  // lifetime including the exit animation. What .makc-open-on-load
  // wants: it hides itself behind any overlay, and it must stay hidden
  // until the overlay is actually gone, not reappear over a panel that
  // is still sliding out.
  //
  // makc-overlay-settled -- "an overlay is up and not yet leaving".
  // What the cart pill wants: its entrance is gated on this, and
  // dropping the class at the START of the exit is what lets the pill's
  // 200ms return overlap the panel's 200ms departure instead of queuing
  // behind it. Waiting for unlock() -- which fires on the panel's
  // transitionend -- made a close read as two motions: the sheet left,
  // and only then, ~20ms later, the pill spent another 200ms arriving in
  // the space it had just vacated.
  //
  // Both were briefly the same class (2026-09-22), which gave
  // .makc-open-on-load the pill's timing and made it flash back over a
  // closing sheet. Split 2026-09-23.
  var OVERLAY_SETTLED_CLASS = 'makc-overlay-settled';

  function syncOverlayClasses() {
    var root = document.documentElement;
    root.classList.toggle(OVERLAY_OPEN_CLASS, lockCount > 0);
    root.classList.toggle(OVERLAY_SETTLED_CLASS, lockCount - closingCount > 0);
  }

  // Safe to call more than once per close, and safe not to call at all --
  // the overlays that close instantly (cart shell, product sheet, search
  // takeover, notify-me) simply never do, and unlock() behaves exactly
  // as before for them.
  function beginClose() {
    if (closingCount >= lockCount) return;
    closingCount += 1;
    syncOverlayClasses();
  }

  function lock() {
    lockCount += 1;
    syncOverlayClasses();
    if (lockCount > 1) return;
    lockedScrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = -lockedScrollY + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';
  }

  function unlock() {
    lockCount = Math.max(0, lockCount - 1);
    // Paired with the beginClose() above, if one was signalled. Reopening
    // mid-close is handled by the arithmetic rather than a special case:
    // lock() raises lockCount again, so lockCount - closingCount goes
    // back above zero and the class returns before the pill can finish
    // arriving.
    if (closingCount > 0) closingCount -= 1;
    syncOverlayClasses();
    if (lockCount > 0) return;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    window.scrollTo(0, lockedScrollY);
  }

  return { lock: lock, unlock: unlock, beginClose: beginClose };
})();
